/**
 * Saved searches persisted in localStorage.
 *
 * Storage contract (shared with GlobalSearch / SavedSearchesDialog):
 * - Key: `@nipsys:global-saved-searches`
 * - Shape: `SavedSearch[]` (newest first, max MAX_SAVED_SEARCHES)
 * - Legacy compat: entries missing `id`/`ts` are repaired on read.
 */
import { useCallback, useEffect, useState } from "react";

const SAVED_KEY = "@nipsys:global-saved-searches";

export const MAX_SAVED_SEARCHES = 20;

export interface SavedSearch {
  id: string;
  /** User-provided label. */
  name: string;
  /** The query to re-execute. */
  query: string;
  /** Epoch millis when the search was saved/renamed (0 for legacy entries). */
  ts: number;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ss-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readSaved(): SavedSearch[] {
  try {
    const stored = localStorage.getItem(SAVED_KEY);
    if (!stored) return [];
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    const out: SavedSearch[] = [];
    for (const raw of parsed) {
      if (!raw || typeof raw !== "object") continue;
      const item = raw as Record<string, unknown>;
      const name = typeof item.name === "string" ? item.name.trim() : "";
      const query = typeof item.query === "string" ? item.query.trim() : "";
      if (!name || !query) continue;
      out.push({
        id: typeof item.id === "string" && item.id ? item.id : newId(),
        name,
        query,
        ts: typeof item.ts === "number" && Number.isFinite(item.ts) ? item.ts : 0,
      });
    }
    return out.slice(0, MAX_SAVED_SEARCHES);
  } catch {
    return [];
  }
}

export interface UseSavedSearchesResult {
  saved: SavedSearch[];
  /**
   * Saves (or renames, if the query is already saved) a search.
   * Returns false when name/query are empty or the list is full.
   */
  save: (name: string, query: string) => boolean;
  /** Removes a saved search by id. */
  remove: (id: string) => void;
}

export function useSavedSearches(): UseSavedSearchesResult {
  const [saved, setSaved] = useState<SavedSearch[]>([]);

  // Hydrate on mount and write the repaired list back: legacy entries get
  // a *stable* id (regenerated ids on every read would make remove(id)
  // silently no-op on the first click). Self-heals before any interaction.
  useEffect(() => {
    const repaired = readSaved();
    setSaved(repaired);
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(repaired));
    } catch {
      // Storage blocked — state is already repaired in memory.
    }
  }, []);

  const persist = useCallback((next: SavedSearch[]) => {
    setSaved(next);
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(next));
    } catch {
      // Storage full/blocked — keep in-memory state, ignore the write failure.
    }
  }, []);

  const save = useCallback(
    (name: string, query: string) => {
      const trimmedName = name.trim();
      const trimmedQuery = query.trim();
      if (!trimmedName || !trimmedQuery) return false;

      const current = readSaved();
      // Re-saving an existing query renames it instead of duplicating.
      const existing = current.find(
        (entry) => entry.query.toLowerCase() === trimmedQuery.toLowerCase(),
      );
      if (existing) {
        persist(
          current.map((entry) =>
            entry.id === existing.id ? { ...entry, name: trimmedName, ts: Date.now() } : entry,
          ),
        );
        return true;
      }
      if (current.length >= MAX_SAVED_SEARCHES) return false;
      persist([
        { id: newId(), name: trimmedName, query: trimmedQuery, ts: Date.now() },
        ...current,
      ]);
      return true;
    },
    [persist],
  );

  const remove = useCallback(
    (id: string) => {
      persist(readSaved().filter((entry) => entry.id !== id));
    },
    [persist],
  );

  return { saved, save, remove };
}
